/**
 * Job Opening — detail.  GET /jobs/:id  +  GET /applications?job_opening_id=
 */

import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, UserPlus, MapPin, Users, CalendarDays, Briefcase } from 'lucide-react';
import { Button, Skeleton } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import { useJob } from '../../features/recruitment/hooks/useJobs';
import { useApplications } from '../../features/recruitment/hooks/useApplications';
import { usePeopleOptions } from '../../features/recruitment/hooks/useOptions';
import { JobStatusBadge, EmptyState, ErrorState, ApplicationsTable } from '../../features/recruitment';
import { EMPLOYMENT_TYPE_META } from '../../features/recruitment/config/constants';
import { formatDate, formatExperienceRange } from '../../features/recruitment/utils/format';

const MetaItem: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
	<div className='flex items-center gap-2'>
		<span className='text-gray-400'>{icon}</span>
		<div>
			<p className='text-[11px] uppercase tracking-wide text-gray-400'>{label}</p>
			<p className='text-sm font-medium text-gray-800'>{value}</p>
		</div>
	</div>
);

export const JobOpeningDetail: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const jobId = id ? Number(id) : undefined;

	const { data: job, isLoading, isError, refetch } = useJob(jobId);
	const { data: apps, isLoading: appsLoading, isError: appsError, refetch: refetchApps } = useApplications(
		jobId ? { job_opening_id: jobId, page_size: 200 } : {}
	);
	const { data: people } = usePeopleOptions();
	const peopleById = useMemo(() => new Map((people ?? []).map(p => [p.value, p.label])), [people]);

	if (isLoading) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl space-y-4'>
					<Skeleton className='h-8 w-64' />
					<Skeleton className='h-40 w-full' />
					<Skeleton className='h-64 w-full' />
				</div>
			</div>
		);
	}

	if (isError || !job) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl'>
					<ErrorState message='We could not load this job opening.' onRetry={() => refetch()} />
				</div>
			</div>
		);
	}

	const managerName = job.hiring_manager_id ? peopleById.get(String(job.hiring_manager_id)) || `#${job.hiring_manager_id}` : '—';
	const rows = apps?.rows ?? [];

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-5xl'>
				<button
					type='button'
					onClick={() => navigate('/recruitment/jobs')}
					className='mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800'
				>
					<ArrowLeft className='h-4 w-4' /> Job Openings
				</button>

				{/* Header card */}
				<Card className='mb-6'>
					<div className='flex flex-wrap items-start justify-between gap-4'>
						<div>
							<div className='flex items-center gap-3'>
								<h1 className='text-2xl font-semibold text-gray-900'>{job.title}</h1>
								<JobStatusBadge status={job.status} />
							</div>
							<p className='mt-1 text-sm text-gray-500'>{EMPLOYMENT_TYPE_META[job.employment_type] ?? job.employment_type}</p>
						</div>
						<div className='flex gap-2'>
							<Button variant='outline' leftIcon={<Pencil className='h-4 w-4' />} onClick={() => navigate(`/recruitment/jobs/${job.id}/edit`)}>
								Edit
							</Button>
							<Button
								variant='primary'
								leftIcon={<UserPlus className='h-4 w-4' />}
								onClick={() => navigate(`/recruitment/candidates/add?job=${job.id}`)}
							>
								Add Candidate
							</Button>
						</div>
					</div>

					<div className='mt-5 grid grid-cols-2 gap-4 border-t border-gray-100 pt-5 md:grid-cols-4'>
						<MetaItem icon={<MapPin className='h-4 w-4' />} label='Location' value={job.facility_key || '—'} />
						<MetaItem icon={<Users className='h-4 w-4' />} label='Openings' value={job.openings_count} />
						<MetaItem icon={<Briefcase className='h-4 w-4' />} label='Experience' value={formatExperienceRange(job.experience_min, job.experience_max)} />
						<MetaItem icon={<Users className='h-4 w-4' />} label='Hiring Manager' value={managerName} />
						<MetaItem icon={<CalendarDays className='h-4 w-4' />} label='Date Opened' value={formatDate(job.date_opened)} />
					</div>
				</Card>

				{/* JD + qualifications */}
				{(job.jd_text || job.qualifications?.required?.length || job.qualifications?.preferred?.length) && (
					<Card className='mb-6 space-y-4'>
						{job.jd_text && (
							<div>
								<h2 className='mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500'>Job Description</h2>
								<p className='whitespace-pre-line text-sm text-gray-700'>{job.jd_text}</p>
							</div>
						)}
						<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
							{!!job.qualifications?.required?.length && (
								<div>
									<h3 className='mb-2 text-sm font-semibold text-gray-700'>Required qualifications</h3>
									<ul className='list-inside list-disc space-y-1 text-sm text-gray-600'>
										{job.qualifications.required.map((q, i) => (
											<li key={i}>{q}</li>
										))}
									</ul>
								</div>
							)}
							{!!job.qualifications?.preferred?.length && (
								<div>
									<h3 className='mb-2 text-sm font-semibold text-gray-700'>Preferred qualifications</h3>
									<ul className='list-inside list-disc space-y-1 text-sm text-gray-600'>
										{job.qualifications.preferred.map((q, i) => (
											<li key={i}>{q}</li>
										))}
									</ul>
								</div>
							)}
						</div>
					</Card>
				)}

				{/* Applications for this opening */}
				<Card>
					<div className='mb-4 flex items-center justify-between'>
						<h2 className='text-base font-semibold text-gray-900'>Applications for this opening</h2>
						<span className='text-sm text-gray-500'>{apps?.total ?? rows.length} total</span>
					</div>

					{appsLoading ? (
						<div className='space-y-2'>
							<Skeleton className='h-10 w-full' />
							<Skeleton className='h-10 w-full' />
							<Skeleton className='h-10 w-full' />
						</div>
					) : appsError ? (
						<ErrorState message='Could not load applications for this opening.' onRetry={() => refetchApps()} />
					) : rows.length === 0 ? (
						<EmptyState
							title='No applications yet'
							message='Add a candidate to this opening to get started.'
							icon={<UserPlus className='h-7 w-7' />}
							action={
								<Button variant='primary' leftIcon={<UserPlus className='h-4 w-4' />} onClick={() => navigate(`/recruitment/candidates/add?job=${job.id}`)}>
									Add Candidate
								</Button>
							}
						/>
					) : (
						<ApplicationsTable rows={rows} hideJobColumn onOpen={appId => navigate(`/recruitment/applications/${appId}`)} />
					)}
				</Card>
			</div>
		</div>
	);
};
